import { Element } from './element';
import { isElement, isTextNode } from './type_symbol';
/** The iterator direction and order */
export var ElementIteratorType;
(function (ElementIteratorType) {
    /** Iterate all ancestors in shadow tree */
    ElementIteratorType["ShadowAncestors"] = "shadow-ancestors";
    /** Iterate all ancestors in composed tree */
    ElementIteratorType["ComposedAncestors"] = "composed-ancestors";
    /** Iterate all descendants in shadow tree, returning parents before their children */
    ElementIteratorType["ShadowDescendantsRootFirst"] = "shadow-descendants-root-first";
    /** Iterate all descendants in shadow tree, returning parents after their children */
    ElementIteratorType["ShadowDescendantsRootLast"] = "shadow-descendants-root-last";
    /** Iterate all descendants in composed tree, returning parents before their children */
    ElementIteratorType["ComposedDescendantsRootFirst"] = "composed-descendants-root-first";
    /** Iterate all descendants in composed tree, returning parents after their children */
    ElementIteratorType["ComposedDescendantsRootLast"] = "composed-descendants-root-last";
})(ElementIteratorType || (ElementIteratorType = {}));
/**
 * An iterator for node tree traversal
 *
 * This iterator is convenient but seems a little slower.
 */
export class ElementIterator {
    /**
     * Create an iterator with type specified
     *
     * The `nodeTypeLimit` is used to limit which kind of nodes will be returned.
     * It limits the returned result by an `instanceof` call.
     * The default value is `Element` ,
     * which means only elements will be returned (text nodes will not).
     * Consider specifying `Object` if text nodes need to be returned as well as elements.
     * Specify `Component` will only return components.
     */
    constructor(node, type, nodeTypeLimit = Element) {
        if (!isElement(node) && !isTextNode(node)) {
            throw new Error('Element iterators can only be used in elements or text nodes');
        }
        this._$node = node;
        this._$nodeTypeLimit = nodeTypeLimit || Element;
        if (type === "shadow-ancestors" /* ElementIteratorType.ShadowAncestors */ ||
            type === "shadow-descendants-root-first" /* ElementIteratorType.ShadowDescendantsRootFirst */ ||
            type === "shadow-descendants-root-last" /* ElementIteratorType.ShadowDescendantsRootLast */) {
            this._$composed = false;
        }
        else if (type === "composed-ancestors" /* ElementIteratorType.ComposedAncestors */ ||
            type === "composed-descendants-root-first" /* ElementIteratorType.ComposedDescendantsRootFirst */ ||
            type === "composed-descendants-root-last" /* ElementIteratorType.ComposedDescendantsRootLast */) {
            this._$composed = true;
        }
        else {
            throw new Error(`Unrecognized iterator type "${String(type)}"`);
        }
        if (type === "shadow-ancestors" /* ElementIteratorType.ShadowAncestors */ ||
            type === "composed-ancestors" /* ElementIteratorType.ComposedAncestors */) {
            this._$isAncestor = true;
        }
        else {
            this._$isAncestor = false;
        }
        if (type === "shadow-descendants-root-first" /* ElementIteratorType.ShadowDescendantsRootFirst */ ||
            type === "composed-descendants-root-first" /* ElementIteratorType.ComposedDescendantsRootFirst */) {
            this._$rootFirst = true;
        }
        else {
            this._$rootFirst = false;
        }
    }
    static create(node, type, nodeTypeLimit) {
        return new ElementIterator(node, type, nodeTypeLimit);
    }
    [Symbol.iterator]() {
        return this._$getIterator();
    }
    *_$getIterator() {
        const nodeTypeLimit = this._$nodeTypeLimit;
        const composed = this._$composed;
        if (this._$isAncestor) {
            let cur = this._$node;
            for (;;) {
                if (cur instanceof nodeTypeLimit) {
                    if ((yield cur) === false)
                        return;
                }
                let next;
                if (composed) {
                    next = cur.getComposedParent();
                }
                else {
                    next = cur.parentNode;
                }
                if (next)
                    cur = next;
                else
                    break;
            }
        }
        else {
            const rootFirst = this._$rootFirst;
            const rec = function* (node) {
                if (rootFirst) {
                    if (node instanceof nodeTypeLimit) {
                        if ((yield node) === false)
                            return;
                    }
                }
                if (isElement(node)) {
                    if (composed) {
                        const iterator = node.iterateComposedChild();
                        for (let it = iterator.next(); !it.done; it = iterator.next()) {
                            yield* rec(it.value);
                        }
                    }
                    else {
                        const childNodes = node.childNodes;
                        for (let i = 0; i < childNodes.length; i += 1) {
                            const child = childNodes[i];
                            yield* rec(child);
                        }
                    }
                }
                if (!rootFirst) {
                    if (node instanceof nodeTypeLimit) {
                        yield node;
                    }
                }
            };
            yield* rec(this._$node);
        }
    }
    forEach(f) {
        const nodeTypeLimit = this._$nodeTypeLimit;
        const composed = this._$composed;
        if (this._$isAncestor) {
            let cur = this._$node;
            for (;;) {
                if (cur instanceof nodeTypeLimit) {
                    if (f(cur) === false)
                        return;
                }
                let next;
                if (composed) {
                    next = cur.getComposedParent();
                }
                else {
                    next = cur.parentNode;
                }
                if (next)
                    cur = next;
                else
                    break;
            }
        }
        else {
            const rootFirst = this._$rootFirst;
            const rec = (node) => {
                if (rootFirst) {
                    if (node instanceof nodeTypeLimit) {
                        if (f(node) === false)
                            return false;
                    }
                }
                if (isElement(node)) {
                    let interrupted = false;
                    const childFn = (child) => {
                        if (rec(child) === false) {
                            interrupted = true;
                            return false;
                        }
                        return true;
                    };
                    if (composed) {
                        node.forEachComposedChild(childFn);
                    }
                    else {
                        node.childNodes.every(childFn);
                    }
                    if (interrupted)
                        return false;
                }
                if (!rootFirst) {
                    if (node instanceof nodeTypeLimit) {
                        if (f(node) === false)
                            return false;
                    }
                }
                return true;
            };
            rec(this._$node);
        }
    }
}
//# sourceMappingURL=element_iterator.js.map